import { Component, HostBinding, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { Fixture } from '@cds/betting-offer';
import { LiveSectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { EventFactory, GridSourceEvent } from '@frontend/sports/grid/core/feature/factories';
import { GridGrouping } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { getEvents } from '@frontend/sports/grid/core/feature/store';
import { LiveUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { EsportTrackingValues, NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { kebabCase, sumBy } from 'lodash-es';
import { finalize, map, mergeMap } from 'rxjs';

import { EpcotConfigService } from '../common/epcot-config.service';
import { LiveEventListService, LiveOverviewModel } from './live-event-list.service';
import { LiveUpcomingListComponent, UpcomingLoader, UpcomingModel, UpcomingMoreLoader } from './live-upcoming-list.component';

interface ShowMoreCount {
    curentCount: number;
    shouldShowMore: boolean;
}

@HooksWireup()
@Component({
    selector: 'ms-live-overview',
    templateUrl: 'live-overview.component.html',
    providers: [ObservableGridFactory],
    standalone: false,
})
export class LiveOverviewComponent extends OnRouteResolve<LiveOverviewModel | undefined> {
    tab: LiveUrlParam;
    tracking: string;
    live: ObservableGrid[];
    upcoming?: UpcomingModel;
    upcomingLoaded: boolean;
    isEsportLoading = false;
    isEpcotEnabled: boolean;
    private shouldShowMoreButton: NumberDictionary<ShowMoreCount> = {};

    @HostBinding('class') className = 'live-overview';
    @ViewChild(LiveUpcomingListComponent) upcomingList: LiveUpcomingListComponent;

    private upcomingThreshold = 10;

    constructor(
        public sitecore: Sitecore,
        private eventService: LiveEventListService,
        private gridFactory: ObservableGridFactory,
        private eventFactory: EventFactory,
        private liveConfig: LiveSectionConfig,
        private trackingService: TrackingService,
        private epcotConfigService: EpcotConfigService,
        route: ActivatedRoute,
    ) {
        super(route);
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    onRouteResolved(model: LiveOverviewModel | undefined): void {
        if (!model) {
            return;
        }

        this.gridFactory.destroy();

        this.tab = model.tab;

        if (this.tab === LiveUrlParam.Esports) {
            model.highlights.sportsOffer.forEach((sport) => {
                this.shouldShowMoreButton[sport.sport.id] = {
                    curentCount: sport.fixtures.length,
                    shouldShowMore: sport.fixtures.length < sport.totalCount,
                };
            });
        }

        this.live = model.highlights.sportsOffer.map((offer) =>
            this.gridFactory.fromResponse(this.getGridId(offer.fixtures), offer.fixtures, { grouping: GridGrouping.None }),
        );
        this.tracking = this.getTracking();

        const events = sumBy(this.live, (current) => getEvents(current.getState()).length);

        if (this.live && events < this.upcomingThreshold) {
            this.showUpcoming(true);
        } else {
            this.resetUpcoming();
        }
    }

    showUpcoming(forceLoad?: boolean): void {
        if (this.tab !== LiveUrlParam.Video || (!forceLoad && this.upcomingLoaded)) {
            return;
        }

        this.upcomingLoaded = true;
        this.upcoming = {
            load: this.loadUpcoming,
            more: this.moreUpcoming,
        };
    }

    private getGridId(fixtures: Fixture[], upcoming?: boolean): string {
        const key = `${fixtures[0].sport.id}/live-${kebabCase(LiveUrlParam[this.tab])}`;

        return upcoming ? key + '/upcoming' : key;
    }

    loadMore(sportId: number, currentGrid: ObservableGrid): void {
        if (this.isEsportLoading) {
            return;
        }

        this.isEsportLoading = true;

        this.eventService
            .getEsports(this.liveConfig.pageSize, this.shouldShowMoreButton[sportId].curentCount, sportId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => (this.isEsportLoading = false)),
            )
            .subscribe((result) => {
                const currentEsport = result?.highlights.sportsOffer[0];
                if (currentEsport?.fixtures.length) {
                    currentGrid.addEvents(currentEsport.fixtures as GridSourceEvent[], undefined, GridGrouping.None);
                    this.shouldShowMoreButton[sportId].curentCount += currentEsport.fixtures.length;
                    this.shouldShowMoreButton[sportId].shouldShowMore = this.shouldShowMoreButton[sportId].curentCount < currentEsport.totalCount;
                } else {
                    this.shouldShowMoreButton[sportId].shouldShowMore = false;
                }
            });

        this.trackShowMoreButtonClick(currentGrid.getState().sport.name);
    }

    shouldShowMore(sportId: number): boolean {
        return !!this.shouldShowMoreButton[sportId]?.shouldShowMore;
    }

    private resetUpcoming(): void {
        this.upcomingLoaded = false;
        this.upcoming = undefined;
    }

    private loadUpcoming: UpcomingLoader = () =>
        this.eventService.getLiveUpcomingStreams().pipe(
            map((highlights) => {
                if (!highlights) {
                    return [];
                }

                return highlights.sportsOffer
                    .filter((offer) => offer.fixtures.length > 0)
                    .map((offer) => ({
                        grid: this.gridFactory.fromResponse(this.getGridId(offer.fixtures, true), offer.fixtures, {
                            grouping: GridGrouping.None,
                        }),
                        count: offer.totalCount,
                    }));
            }),
        );

    private moreUpcoming: UpcomingMoreLoader = (skip, sport) =>
        this.eventService.getUpcomingFixtures({ skip }).pipe(mergeMap((page) => this.eventFactory.create(page?.fixtures)));

    private getTracking(): string {
        //TODO add missing enum values to the dictionary and remove below cast
        const trackingMap = {
            [LiveUrlParam.Highlights]: 'Highlights',
            [LiveUrlParam.Video]: 'Video',
            [LiveUrlParam.Esports]: EsportTrackingValues.Esports,
        };

        return `LivePage/${trackingMap[this.tab as keyof typeof trackingMap]}`;
    }

    private trackShowMoreButtonClick(gridName: string): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: EsportTrackingValues.Esports,
            [trackingConstants.COMPONENT_LABEL_EVENT]: EsportTrackingValues.LivePage,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: gridName,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: EsportTrackingValues.LivePageShowMoreCta,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }
}
